import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '@/lib/api'
import { useSessionStore } from '@/stores/session.store'
import { authApi } from '@/features/auth/auth.api'
import { useFlagsStore } from '@/features/flags/flags.store'
import { useCapabilitiesStore } from '@/features/capabilities/capabilities.store'
import { useSubscriptionStore } from '@/features/subscription/subscription.store'
import { useConnectivityStore } from '@/features/connectivity/connectivity.store'
import type { UserSession } from '@/types/models'

// Start-up: if a token pair is stored, ask who it belongs to, then load home data within a 20 s budget.
// Offline and server failures are retryable; a rejected token means guest; home data failing never stops boot.
export type BootPhase = 'loading' | 'ready' | 'offline' | 'error'

export const BOOT_TIMEOUT_MS = 20_000

export async function loadHome(user: UserSession): Promise<void> {
  // Synchronous and first: the capabilities rode in on the /auth/me body this was called with, so
  // there is nothing to fetch, and every gated screen must have its answer before the home renders.
  useCapabilitiesStore.getState().setFromUser(user)
  const results = await Promise.allSettled([useFlagsStore.getState().load(), useSubscriptionStore.getState().loadForUser(user)])
  results.forEach((r) => r.status === 'rejected' && console.warn('loadHome step failed', r.reason))
}

export function clearHome(): void {
  useFlagsStore.getState().clear()
  useCapabilitiesStore.getState().clear()
  useSubscriptionStore.getState().clear()
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(() => reject(new DOMException('Boot timed out', 'TimeoutError')), ms)
    promise.then(resolve, reject).finally(() => window.clearTimeout(id))
  })
}

export function useBoot(): { phase: BootPhase; retry: () => void } {
  const [phase, setPhase] = useState<BootPhase>('loading')
  const running = useRef(false)

  const run = useCallback(async () => {
    if (running.current) return
    running.current = true
    setPhase('loading')
    const { hasTokens, setUser, signOut } = useSessionStore.getState()
    try {
      if (!hasTokens()) {
        setUser(null)
        clearHome()
        setPhase('ready')
        return
      }
      const controller = new AbortController()
      const user = await withTimeout(
        (async () => {
          const u = await authApi.me(controller.signal)
          await loadHome(u)
          return u
        })(),
        BOOT_TIMEOUT_MS,
      ).catch((e) => {
        controller.abort()
        throw e
      })
      setUser(user)
      setPhase('ready')
    } catch (error) {
      if ((error instanceof ApiError && error.status === 401) || !hasTokens()) {
        // Rejected token (or a refresh that failed and already cleared the pair): boot as a guest.
        signOut()
        clearHome()
        setPhase('ready')
      } else if ((error instanceof ApiError && error.isNetwork) || (error instanceof DOMException && error.name === 'TimeoutError')) {
        setPhase('offline')
      } else {
        setPhase('error')
      }
    } finally {
      running.current = false
    }
  }, [])

  useEffect(() => {
    void run()
  }, [run])

  // Come back by itself when the connection returns.
  useEffect(() => {
    if (phase !== 'offline') return
    const onOnline = () => void run()
    window.addEventListener('online', onOnline)
    const off = useConnectivityStore.getState().onRestored(onOnline)
    return () => {
      window.removeEventListener('online', onOnline)
      off()
    }
  }, [phase, run])

  return { phase, retry: run }
}
